// Lado del gasto del evento y del venue. Espejo de `utils/valorEvento.js`:
// ahí vive todo lo que entra (cotización, pagos), aquí todo lo que sale.
// Los cálculos derivan siempre de estos helpers y nunca de campos crudos,
// para que la math del P&L viva en un solo lugar.
//
// TODO COSTO ES FLUJO REAL DE DINERO. Hubo un segundo tipo —el consumo
// imputado de inventario ya comprado— y con él dos nociones de gasto que
// había que no mezclar. Se retiró junto con el inventario consumible: lo que
// se compra y lo que se recarga entra como gasto cuando se paga, y ya.

import { getValorEvento } from "./valorEvento";
import { calendarDayAnchor } from "./dates";

// La comisión del planner es un gasto del evento como cualquier otro (baja
// su utilidad: al final es costo de adquisición), pero el ledger la etiqueta
// aparte porque casi siempre es una transferencia que hay que hacerle al
// planner, y el venue quiere verla como tal (FRA-345).
export const CATEGORIA_COMISION_PLANNER = "Comisión de planner";

export const esComisionPlanner = (costo) =>
  costo?.categoria === CATEGORIA_COMISION_PLANNER;

// Lista fija en código; categorías editables por venue quedan para v2.
// Un RETIRO no es un costo y no está aquí: es repartición de utilidades, vive
// en su propia colección (venues/{id}/retiros) y solo el ledger lo conoce
// (FRA-343). Por eso no se puede capturar en un evento ni en un gasto.
export const CATEGORIAS_COSTO = [
  "Proveedores",
  CATEGORIA_COMISION_PLANNER,
  "Personal",
  "Insumos",
  "Equipo",
  "Mantenimiento",
  "Renta",
  "Servicios",
  "Marketing",
  "Otro",
];

export const ESTADOS_COSTO = ["por_pagar", "pagado"];

// Fecha en que el dinero salió (FRA-342). Nula mientras el costo esté por
// pagar — un compromiso no tiene fecha de pago. Lectura tolerante: un costo
// pagado sin `fechaPago` (anterior a la separación de fechas) se lee usando
// su fecha de causación, que era lo que la única fecha significaba entonces.
export const fechaPagoDe = (costo) => {
  if ((costo?.estado || "por_pagar") !== "pagado") return null;
  return costo.fechaPago || costo.fecha || null;
};

const monto = (costo) => Number(costo?.monto) || 0;

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// filtro: { eventId, estado, desde, hasta, timeZone }
// `eventId: null` filtra gastos generales del venue (no "sin filtro"): la
// distinción es por presencia de la llave, no por su valor.
// desde/hasta son Date y se comparan contra el ancla de día calendario del
// venue, igual que Cuentas — un costo del 31 a las 11pm cae en su mes.
export function filtrarCostos(costos, filtro = {}) {
  const lista = Array.isArray(costos) ? costos : [];
  const { estado, desde, hasta, timeZone } = filtro;
  return lista.filter((c) => {
    if (has(filtro, "eventId") && (c?.eventId ?? null) !== (filtro.eventId ?? null))
      return false;
    if (estado && (c?.estado || "por_pagar") !== estado) return false;
    if (desde || hasta) {
      const anchor = calendarDayAnchor(c?.fecha, timeZone);
      if (!anchor) return false;
      if (desde && anchor < desde) return false;
      if (hasta && anchor > hasta) return false;
    }
    return true;
  });
}

export function totalCostos(costos, filtro = {}) {
  return filtrarCostos(costos, filtro).reduce((sum, c) => sum + monto(c), 0);
}

export function costosDeEvento(costos, eventId) {
  if (!eventId) return [];
  return filtrarCostos(costos, { eventId });
}

export function gastosGenerales(costos, filtro = {}) {
  return filtrarCostos(costos, { ...filtro, eventId: null });
}

// Lo que el venue DEBE.
export function costosPorPagar(costos, filtro = {}) {
  return filtrarCostos(costos, { ...filtro, estado: "por_pagar" });
}

// Un solo groupBy en memoria para pintar utilidad en listas largas de
// eventos sin recorrer los costos una vez por fila.
export function costosPorEvento(costos) {
  const out = {};
  (Array.isArray(costos) ? costos : []).forEach((c) => {
    const eventId = c?.eventId;
    if (!eventId) return;
    const acc =
      out[eventId] ||
      (out[eventId] = { total: 0, porPagar: 0, count: 0 });
    const m = monto(c);
    acc.total += m;
    acc.count += 1;
    if ((c?.estado || "por_pagar") === "por_pagar") acc.porPagar += m;
  });
  return out;
}

// Todo lo que la UI necesita del evento en una sola pasada. `costos` puede
// ser la colección completa del venue o solo la del evento: filtra por
// `event.id` de todos modos.
export function resumenEvento(event, costos) {
  const valor = getValorEvento(event);
  const propios = costosDeEvento(costos, event?.id);
  const total = propios.reduce((s, c) => s + monto(c), 0);
  const porPagar = propios
    .filter((c) => (c?.estado || "por_pagar") === "por_pagar")
    .reduce((s, c) => s + monto(c), 0);
  const utilidad = valor - total;
  return {
    valor,
    costos: total,
    porPagar,
    count: propios.length,
    utilidad,
    // null (no 0) cuando no hay valor capturado: un margen de 0% y "todavía
    // no sabemos" son cosas distintas y la UI las pinta distinto.
    margen: valor > 0 ? utilidad / valor : null,
  };
}

export function utilidadEvento(event, costos) {
  return resumenEvento(event, costos).utilidad;
}

export function margenEvento(event, costos) {
  return resumenEvento(event, costos).margen;
}

export function costosPorCategoria(costos) {
  const map = {};
  (Array.isArray(costos) ? costos : []).forEach((c) => {
    const cat = c?.categoria || "Otro";
    const acc = map[cat] || (map[cat] = { categoria: cat, total: 0, count: 0 });
    acc.total += monto(c);
    acc.count += 1;
  });
  return Object.values(map).sort((a, b) => b.total - a.total);
}

// De dónde sale el costo de una línea del desglose.
//
// Con el modelo de tres dimensiones (FRA-347) la respuesta la da el propio
// servicio: si lo da un proveedor, el costo es su estructura de costo; si es
// del venue, el servicio ES la cosa que cuesta (o no cuesta nada, y aun así
// la línea se le atribuye para poder contestar "¿cuánto vendí de esto?").
//
// Orden de preferencia: link directo puesto a mano (compatibilidad con las
// cotizaciones que ya existen) → el servicio del catálogo → nada.
export function respaldoDeLinea(item, conceptosPorId = {}) {
  if (item?.proveedorId) return { tipo: "proveedor", id: item.proveedorId };
  const concepto = item?.conceptoId ? conceptosPorId[item.conceptoId] : null;
  if (!concepto) return null;
  if (concepto.proveedorId) return { tipo: "proveedor", id: concepto.proveedorId };
  return { tipo: "catalogo", id: concepto.id };
}

export function respaldoDeCosto(costo) {
  if (costo?.proveedorId) return { tipo: "proveedor", id: costo.proveedorId };
  const catalogoId = costo?.conceptoId;
  if (catalogoId) return { tipo: "catalogo", id: catalogoId };
  return null;
}

const claveRespaldo = (r) => (r ? `${r.tipo}:${r.id}` : null);

// Margen por renglón: proveedores contratados y cosas propias del venue en la
// misma tabla, porque la pregunta del owner es la misma — "esta línea,
// ¿cuánto me deja?".
//
// DECISIÓN DE IVA: `cobrado` es el subtotal SIN IVA de las líneas ligadas —
// el IVA que se le cobra al cliente no es ingreso del venue. La utilidad
// total del evento sí usa getValorEvento (con IVA si aplica); son dos
// preguntas distintas.
export function margenPorRespaldo(event, costos, opciones = {}) {
  // `estimadoPorProveedor` es lo que la cotización dice que te va a costar y
  // todavía no está capturado (ver utils/cotizacionProveedores.js). Sin él,
  // un evento viejo con la ambulancia en el desglose y sin su costo enseña
  // "Margen 100%", que es justo la mentira que esta tabla existe para evitar.
  const { conceptos = [], proveedores = [], estimadoPorProveedor = {} } = opciones;
  const desglose = Array.isArray(event?.desglose) ? event.desglose : [];

  const conceptosPorId = {};
  (Array.isArray(conceptos) ? conceptos : []).forEach((c) => {
    if (c?.id) conceptosPorId[c.id] = c;
  });
  const nombrePorClave = {};
  (Array.isArray(conceptos) ? conceptos : []).forEach((c) => {
    if (c?.id) nombrePorClave[`catalogo:${c.id}`] = c.nombre || "";
    // El servicio también sabe cómo se llama su proveedor, y ese es el ÚNICO
    // nombre disponible cuando el proveedor respalda una línea de la
    // cotización pero todavía no está asignado al evento — que es justo el
    // caso que la tabla existe para delatar. Sin esto pintaba el id crudo del
    // documento. Lo de abajo (los asignados) pisa esto si ambos existen.
    if (c?.proveedorId && c?.proveedorNombre) {
      nombrePorClave[`proveedor:${c.proveedorId}`] = c.proveedorNombre;
    }
  });
  (Array.isArray(proveedores) ? proveedores : []).forEach((p) => {
    if (p?.id) nombrePorClave[`proveedor:${p.id}`] = p.nombre || "";
  });

  const rows = {};
  const row = (respaldo, nombre) => {
    const key = claveRespaldo(respaldo);
    const r =
      rows[key] ||
      (rows[key] = {
        key,
        tipo: respaldo.tipo,
        id: respaldo.id,
        proveedorId: respaldo.tipo === "proveedor" ? respaldo.id : null,
        proveedorNombre: "",
        nombre: nombrePorClave[key] || "",
        cobrado: 0,
        costo: 0,
        lineas: 0,
      });
    if (!r.nombre && nombre) r.nombre = nombre;
    if (r.tipo === "proveedor") r.proveedorNombre = r.nombre;
    return r;
  };

  costosDeEvento(costos, event?.id).forEach((c) => {
    const respaldo = respaldoDeCosto(c);
    if (!respaldo) return;
    row(respaldo, c.proveedorNombre || c.conceptoNombre).costo +=
      monto(c);
  });

  desglose.forEach((item) => {
    const respaldo = respaldoDeLinea(item, conceptosPorId);
    if (!respaldo) return;
    const r = row(respaldo);
    r.cobrado += Number(item.units) * Number(item.unitPrice) || 0;
    r.lineas += 1;
  });

  // Un servicio puro del venue (renta de salón) sin costos registrados no
  // tiene margen que mostrar: cobrar $45,000 con costo $0 no es información,
  // es ruido. Se listan los proveedores, lo que sí costó algo y lo que lleva
  // inventario (ahí el costo llega por consumo o recarga).
  const conInventario = (id) => !!conceptosPorId[id]?.itemizado;

  return Object.values(rows)
    .filter((r) => r.tipo === "proveedor" || r.costo > 0 || conInventario(r.id))
    .map((r) => {
      // El estimado se suma al costo para el margen, pero viaja aparte para
      // que la UI pueda decir cuánto de eso todavía no está capturado.
      const costoEstimado =
        r.tipo === "proveedor" ? Number(estimadoPorProveedor[r.id]) || 0 : 0;
      const costoTotal = r.costo + costoEstimado;
      return {
        ...r,
        nombre: r.nombre || r.id,
        costoEstimado,
        costoTotal,
        margen: r.cobrado - costoTotal,
        // Sin líneas ligadas no hay nada contra qué comparar: la UI muestra el
        // costo solo, con el hint para ligarlo, no un -100% engañoso.
        margenPct:
          r.lineas > 0 && r.cobrado > 0 ? (r.cobrado - costoTotal) / r.cobrado : null,
      };
    })
    .sort((a, b) => b.costoTotal - a.costoTotal);
}

// Vista de solo proveedores. Se mantiene porque es la pregunta más común y
// porque la UI de fase 1 la usaba directamente.
export function margenPorProveedor(event, costos, opciones = {}) {
  return margenPorRespaldo(event, costos, opciones).filter(
    (r) => r.tipo === "proveedor"
  );
}

// ── Cancelación del evento y saldo a favor ─────────────────────────────
//
// Cancelar un evento tiene dos consecuencias del lado del gasto:
//   - Lo POR PAGAR ya no se debe: el compromiso nace del evento y muere con
//     él. Se elimina (queda constancia en el evento y en la bitácora); al
//     reactivar, la cotización vuelve a generar sus costos automáticos.
//   - Lo YA PAGADO a un proveedor no regresa solo: queda como SALDO A FAVOR
//     con ese proveedor (`costo.saldoAFavor`), que el owner resuelve desde
//     la ficha del proveedor: lo aplica a otro evento (el costo se muda a
//     ese evento, un solo gasto, sin duplicar) o lo marca devuelto (entra
//     como devolución al ledger). Lo pagado sin proveedor (personal, insumos)
//     es gasto hundido y no se toca.
//
// saldoAFavor: { estado: 'activo' | 'aplicado' | 'devuelto', monto,
//   eventId, eventoNombre, createdAt, resueltoAt?, aplicadoEventId?,
//   aplicadoEventoNombre?, montoDevuelto?, fechaDevolucion?, notas? }
export const ESTADOS_SALDO_A_FAVOR = ["activo", "aplicado", "devuelto"];

export const saldoAFavorDe = (costo) =>
  costo?.saldoAFavor && typeof costo.saldoAFavor === "object" ? costo.saldoAFavor : null;

export const tieneSaldoAFavorActivo = (costo) =>
  saldoAFavorDe(costo)?.estado === "activo";

// Costos con saldo a favor (en cualquier estado), opcionalmente de un
// proveedor. Los activos primero, luego por fecha de creación descendente.
export function saldosAFavor(costos, { proveedorId = null } = {}) {
  const ms = (v) =>
    v?.toDate ? v.toDate().getTime() : v?.seconds ? v.seconds * 1000 : v ? new Date(v).getTime() || 0 : 0;
  return (Array.isArray(costos) ? costos : [])
    .filter((c) => saldoAFavorDe(c) && (!proveedorId || c.proveedorId === proveedorId))
    .sort((a, b) => {
      const aa = tieneSaldoAFavorActivo(a) ? 0 : 1;
      const bb = tieneSaldoAFavorActivo(b) ? 0 : 1;
      if (aa !== bb) return aa - bb;
      return ms(saldoAFavorDe(b).createdAt) - ms(saldoAFavorDe(a).createdAt);
    });
}

export function totalSaldoAFavor(costos, { proveedorId = null } = {}) {
  return saldosAFavor(costos, { proveedorId })
    .filter(tieneSaldoAFavorActivo)
    .reduce((s, c) => s + monto(c), 0);
}

// Lo que el proveedor devolvió de un saldo a favor: entra a caja con su
// fecha. `montoDevuelto` ausente = devolución completa.
export function devolucionDe(costo) {
  const s = saldoAFavorDe(costo);
  if (!s || s.estado !== "devuelto") return null;
  const devuelto = s.montoDevuelto == null ? monto(costo) : Number(s.montoDevuelto) || 0;
  if (devuelto <= 0) return null;
  return { monto: devuelto, fecha: s.fechaDevolucion || s.resueltoAt || null };
}

// Qué le pasa a cada costo del evento al cancelarlo. Puro: el caller escribe.
export function planCancelacionCostos(costos, eventId) {
  const propios = costosDeEvento(costos, eventId);
  const liberar = propios.filter((c) => (c?.estado || "por_pagar") === "por_pagar");
  const pagados = propios.filter((c) => (c?.estado || "por_pagar") === "pagado");
  // Un saldo ya resuelto (aplicado/devuelto) no se vuelve a abrir.
  const saldos = pagados.filter((c) => c.proveedorId && !saldoAFavorDe(c));
  const hundidos = pagados.filter((c) => !c.proveedorId);
  const suma = (l) => l.reduce((s, c) => s + monto(c), 0);
  return {
    liberar,
    saldos,
    hundidos,
    totalLiberar: suma(liberar),
    totalSaldos: suma(saldos),
    totalHundidos: suma(hundidos),
  };
}
